import type { BookingDecision, BookingStatus, ListingInput } from '@quickreport/domain';
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

/**
 * The lab's public page and the bookings people make through it. Kept on the server only (not in
 * the lab's local database), so these screens need the internet.
 */

export interface Booking {
  id: string;
  code: string;
  contact: string;
  patientName: string;
  patientAge: string;
  patientSex: string;
  kind: 'visit' | 'home';
  tests: { templateId: string; name: string; pricePaise: number | null }[];
  totalPaise: number;
  feePaise: number;
  reportDelivery: boolean;
  date: string;
  time: string;
  address: string;
  note: string;
  status: BookingStatus;
  labComment: string;
  cancelReason: string;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarDay {
  date: string;
  pending: number;
  approved: number;
  home: number;
  total: number;
}

export interface ListingState {
  listing: ListingInput;
  fromReport: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logoFileId: string | null;
  };
  located: boolean;
  problems: string[];
  tests: { id: string; name: string; department: string | null; pricePaise: number | null }[];
  updatedAt: string | null;
}

export const bookingsApi = {
  list: (q: { from?: string; to?: string; status?: BookingStatus }) => {
    const p = new URLSearchParams(
      Object.entries(q).filter((e): e is [string, string] => Boolean(e[1])),
    );
    return api<{ items: Booking[]; pending: number }>('GET', `/v1/bookings?${p}`);
  },
  calendar: (month: string) =>
    api<{ days: CalendarDay[] }>('GET', `/v1/bookings/calendar?month=${month}`),
  decide: (id: string, d: Partial<BookingDecision> & { approve: boolean }) =>
    api<Booking>('POST', `/v1/bookings/${id}/decide`, d),
  done: (id: string) => api<Booking>('POST', `/v1/bookings/${id}/done`),
  listing: () => api<ListingState>('GET', '/v1/listing'),
  saveListing: (l: ListingInput) =>
    api<{ ok: true; located: boolean; problems: string[] }>('PUT', '/v1/listing', l),
};

/**
 * How many bookings wait for an answer, for the badge in the menu. Asked every minute while the
 * app is open; nothing shows when offline or for people who do not handle bookings.
 */
export function usePendingBookings(enabled: boolean): { pending: number; refresh: () => void } {
  const [pending, setPending] = useState(0);
  const refresh = useCallback(() => {
    if (!enabled) return;
    bookingsApi
      .list({ status: 'pending' })
      .then((r) => setPending(r.pending))
      .catch(() => undefined);
  }, [enabled]);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('labview:bookings', refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('labview:bookings', refresh);
    };
  }, [refresh]);
  return { pending, refresh };
}

/** Tell the menu badge that bookings changed. */
export const bookingsChanged = () => window.dispatchEvent(new Event('labview:bookings'));
